import React from 'react';
import { useHistory } from 'react-router-dom';
import { generatePath, useLocation, useRouteMatch } from 'react-router';
import { Helmet } from 'react-helmet-async';

import 'client/styles/index.scss';

import { useAppDispatch, useAppSelector, useHideAccessabilityNotifyText, useResponsive } from 'client/hooks';
import { checkRedirectSignIn, getUserData, me } from 'client/redux/user/actions';
import { selectAuth, selectUserId, selectUser } from 'client/redux/user/selectors';
import { usePrev, useCookieConsent, useAppcues } from 'client/hooks';
import * as routes from 'client/constants/routes';

import { BASE_CLIENT_URL, INTERCOM_APP_ID } from 'client/constants';
import { SUPPORTED_LANGUAGES } from 'constants/index';

import shareImg from 'client/assets/shareimage.png';
import favicon16 from 'client/assets/favicon/web/16x16/favicon.png';
import favicon from 'client/assets/favicon/web/favicon.ico';
import favicon32 from 'client/assets/favicon/web/32x32/favicon.png';
import faviconApple from 'client/assets/favicon/apple/favicon.png';

import DesktopApp from './Desktop';
import MobileApp from './Mobile';

const App: React.FC = () => {
  const [isMobile] = useResponsive(['MOBILE']);
  const location = useLocation();
  const appDispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const auth = useAppSelector(selectAuth);
  const user = useAppSelector(selectUser);
  const prevUserId = usePrev(userId);

  const history = useHistory();
  const isSignInRoute = !!useRouteMatch<{ id: string }>(routes.SIGN_IN_ROUTE);
  const isSignUpRoute = !!useRouteMatch<{ id: string }>(routes.SIGN_UP_ROUTE);
  const companyRoute = useRouteMatch<{ id: string }>({ path: routes.COMPANY_PAGE_ROUTE, exact: true });

  const removePreloaderTimeoutRef = React.useRef<NodeJS.Timeout | number | null>(null);

  useAppcues();
  useHideAccessabilityNotifyText();
  useCookieConsent();

  React.useEffect(() => {
    if (auth.isLoggedIn && (isSignInRoute || isSignUpRoute)) {
      history.replace(routes.ROOT);
    }
    if (auth.isLoggedIn && user) {
      (window as any).Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: INTERCOM_APP_ID,
        user_id: user._id,
        user_hash: user.intercomHash,
      });
    }
  }, [auth.isLoggedIn]);

  React.useEffect(() => {
    const checkSignIn = async () => {
      if (isMobile === undefined) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);

      const isLinkedInRedirect = urlParams.get('provider') === 'linkedin';
      const isCheckRedirectSignInRequired = window.localStorage.getItem('isCheckRedirectSignInRequired') === 'true';
      let isSignedInByRedirect = false;

      if ((isLinkedInRedirect || (isCheckRedirectSignInRequired && isMobile)) && !userId) {
        const signInResult = await appDispatch(checkRedirectSignIn());
        isSignedInByRedirect = signInResult.meta.requestStatus === 'fulfilled' && signInResult.payload === true;
        window.localStorage.setItem('isCheckRedirectSignInRequired', 'false');

        if (isSignedInByRedirect && (isSignInRoute || isSignUpRoute)) {
          history.replace(routes.ROOT);
        }

        const isRedirectUrlRequired = window.localStorage.getItem('redirectUrl');
        if (isRedirectUrlRequired && isRedirectUrlRequired.length !== 0) {
          history.replace(isRedirectUrlRequired);
          window.localStorage.removeItem('redirectUrl');
        }

        if (isLinkedInRedirect) {
          console.info('LinkedIn redirect sign-in flow completed, removing provider param from URL...');
          const urlParams = new URLSearchParams(history.location.search);
          urlParams.delete('provider');
          urlParams.delete('state');
          history.replace(history.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : ''));
        }
      }

      if (!isSignedInByRedirect && !userId && !prevUserId) {
        const meResult = await appDispatch(me());

        if (meResult.meta.requestStatus === 'rejected') {
          console.log('Client is unauthorized...');
        } else {
          appDispatch(getUserData({ target: 'all' }));
        }
      } else if (!isSignedInByRedirect && userId) {
        appDispatch(getUserData({ target: 'all' }));
      }

      removeRootPreloader();
    };

    checkSignIn();
  }, [appDispatch, userId, isMobile]);

  const removeRootPreloader = () => {
    const preloadingScreen: HTMLElement | null = document.querySelector('.main-preloader');

    if (preloadingScreen) {
      setTimeout(() => {
        preloadingScreen.style.opacity = '0';
        preloadingScreen.style.visibility = 'hidden';
        preloadingScreen.style.pointerEvents = 'none';
      }, 500);

      if (removePreloaderTimeoutRef.current) clearTimeout(removePreloaderTimeoutRef.current);
      removePreloaderTimeoutRef.current = setTimeout(() => {
        if (document && document.body) {
          document.body.removeChild(preloadingScreen);
        }
      }, 1500);
    }
  };

  const renderHelmet = () => {
    const seo = {
      title: `Catalyst Investors' Club`,
      description: `Catalyst Investors' Club – Discover the most disruptive startups and exclusive early-stage investment opportunities. Enabling accredited investors to invest alongside VCs.`,
    };
    const og = {
      title: `Catalyst Investors' Club - Exclusive Opportunities to Invest in Startups`,
      description: `Discover the most disruptive startups, find exclusive investment opportunities, and join Catalyst Investors' Club to invest alongside reputable lead investors.`,
      image: shareImg,
    };

    return (
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={og.title} />
        <meta property="og:description" content={og.description} />
        <meta property="og:image" content={og.image} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="Shortcut Icon" type="image/x-icon" href={favicon} />
        <link rel="apple-touch-icon" sizes="180x180" href={faviconApple} />
        <link rel="icon" type="image/png" sizes="32x32" href={favicon32} />
        <link rel="icon" type="image/png" sizes="16x16" href={favicon16} />
        <link rel="canonical" href={`${BASE_CLIENT_URL}${location.pathname}`} />
        {companyRoute &&
          SUPPORTED_LANGUAGES.map((lang) => (
            <link
              key={lang}
              rel="alternate"
              hrefLang={lang}
              href={`${BASE_CLIENT_URL}${generatePath(routes.COMPANY_PAGE_ROUTE, {
                lang,
                id: companyRoute.params.id,
              })}`}
            />
          ))}
      </Helmet>
    );
  };

  return (
    <>
      {renderHelmet()}
      {isMobile ? <MobileApp /> : <DesktopApp />}
    </>
  );
};

export default App;
